import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  ViewChild,
} from '@angular/core';
import { Margin } from '@freelancer/ui/margin';

export enum FontType {
  PARAGRAPH = 'paragraph',
  SPAN = 'span',
  STRONG = 'strong',
  CONTAINER = 'container',
}

export enum FontSize {
  XSMALL = 'tiny',
  SMALL = 'detail',
  MID = 'ui',
  LARGE = 'landing',
  INHERIT = 'inherit',
}

export enum FontColor {
  DARK = 'dark',
  MID = 'mid',
  LIGHT = 'light',
  ERROR = 'error',
  SUCCESS = 'success',
  WARNING = 'warning',
  INHERIT = 'inherit',
  PROMO = 'promo',
  RECRUITER = 'recruiter',
}

export enum FontWeight {
  BOLD = 'bold',
  NORMAL = 'normal',
}

export enum FontStyle {
  ITALIC = 'italic',
  NORMAL = 'normal',
}

export enum TextAlign {
  CENTER = 'center',
  LEFT = 'left',
  RIGHT = 'right',
}

export enum TextTransform {
  CAPITALIZE = 'capitalize',
  LOWERCASE = 'lowercase',
  UPPERCASE = 'uppercase',
}

@Component({
  selector: 'fl-text',
  template: `
    <ng-container [ngSwitch]="fontType">
      <p
        class="NativeElement"
        #content
        *ngSwitchCase="FontType.PARAGRAPH"
        [attr.data-text-transform]="textTransform"
        [attr.data-color]="color"
        [attr.data-size]="size"
        [attr.data-size-tablet]="sizeTablet"
        [attr.data-size-desktop]="sizeDesktop"
        [attr.data-weight]="weight"
        [attr.data-style]="style"
        [attr.data-line-break]="lineBreakEnabled"
        [flMarginRight]="isTruncatable ? Margin.XXSMALL : Margin.NONE"
      >
        <ng-container *ngTemplateOutlet="injectedContent"></ng-container>
      </p>
      <span
        class="NativeElement"
        #content
        *ngSwitchCase="FontType.SPAN"
        [attr.data-text-transform]="textTransform"
        [attr.data-color]="color"
        [attr.data-size]="size"
        [attr.data-size-tablet]="sizeTablet"
        [attr.data-size-desktop]="sizeDesktop"
        [attr.data-weight]="weight"
        [attr.data-style]="style"
        [attr.data-line-break]="lineBreakEnabled"
        [flMarginRight]="isTruncatable ? Margin.XXSMALL : Margin.NONE"
      >
        <ng-container *ngTemplateOutlet="injectedContent"></ng-container>
      </span>
      <strong
        class="NativeElement Strong"
        #content
        *ngSwitchCase="FontType.STRONG"
        [attr.data-text-transform]="textTransform"
        [attr.data-size]="size"
        [attr.data-size-tablet]="sizeTablet"
        [attr.data-size-desktop]="sizeDesktop"
        [attr.data-weight]="weight"
        [attr.data-line-break]="lineBreakEnabled"
        [flMarginRight]="isTruncatable ? Margin.XXSMALL : Margin.NONE"
      >
        <ng-container *ngTemplateOutlet="injectedContent"></ng-container>
      </strong>
      <div
        class="NativeElement"
        #content
        *ngSwitchCase="FontType.CONTAINER"
        [attr.data-text-transform]="textTransform"
        [attr.data-color]="color"
        [attr.data-size]="size"
        [attr.data-size-tablet]="sizeTablet"
        [attr.data-size-desktop]="sizeDesktop"
        [attr.data-weight]="weight"
        [attr.data-line-break]="lineBreakEnabled"
        [flMarginRight]="isTruncatable ? Margin.XXSMALL : Margin.NONE"
      >
        <ng-container *ngTemplateOutlet="injectedContent"></ng-container>
      </div>
      <ng-template #injectedContent> <ng-content></ng-content> </ng-template>
    </ng-container>
    <fl-link *ngIf="isTruncatable" (click)="toggleTruncatedText()">
      <ng-container *ngIf="isTruncated" i18n="Read more button">
        Read More
      </ng-container>
      <ng-container *ngIf="!isTruncated" i18n="Read less button">
        Read Less
      </ng-container>
    </fl-link>
  `,
  styleUrls: ['./text.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextComponent implements AfterViewInit {
  FontType = FontType;
  Margin = Margin;

  @Input() color = FontColor.DARK;

  /** Font size for mobile and above */
  @Input() size = FontSize.MID;

  /** Change the [size] from tablet and above */
  @Input() sizeTablet?: FontSize;

  /** Change the [size] and/or [sizeTablet] from desktop and above */
  @Input() sizeDesktop?: FontSize;

  @Input() style = FontStyle.NORMAL;

  /** Change the text-align property (only works on fontType.PARAGRAPH) */
  @HostBinding('attr.data-text-align')
  @Input()
  textAlign?: TextAlign;

  @Input() textTransform?: TextTransform;

  @Input() weight = FontWeight.NORMAL;

  /** Defines the HTML node it will use e.g (<p>, <span>, <strong>)
   *  Note: This is for semantics usage which defaults to a <p> tag
   */
  @HostBinding('attr.data-type')
  @Input()
  fontType = FontType.PARAGRAPH;

  @ViewChild('content', { read: ElementRef })
  content: ElementRef<HTMLElement>;

  /** Character limit to truncate text */
  @HostBinding('attr.data-read-more')
  @Input()
  readMore? = 0;

  get lineBreakEnabled(): boolean {
    return this.displayLineBreaks && (!this.isTruncatable || !this.isTruncated);
  }

  /**
   * Default false, true will parse the \n, <br> tags in the string to create a new line
   * Will break the line when readmore=false, or readmore=true and readmore clicked
   */
  @Input()
  displayLineBreaks = false;

  isTruncatable = false;
  isTruncated = false;
  originalText: string;
  truncatedText: string;

  constructor(private changeDetector: ChangeDetectorRef) {}

  ngAfterViewInit() {
    if (this.readMore) {
      this.changeDetector.markForCheck();
      this.originalText = this.content.nativeElement.innerText.trim();
      this.truncatedText = `${this.originalText.slice(0, this.readMore)}...`;
      this.isTruncatable = this.originalText.length > this.readMore;
      this.toggleTruncatedText();
      this.changeDetector.detectChanges();
    }
  }

  toggleTruncatedText() {
    if (this.isTruncatable) {
      this.isTruncated = !this.isTruncated;
      this.content.nativeElement.textContent = this.isTruncated
        ? this.truncatedText
        : this.originalText;
    }
  }
}
